import { pick } from 'lodash';
import { isSameDay, isValid, toDate } from 'date-fns';
import {
  Address,
  AddressModelKeys,
  AttendeeWizardState,
  Conference,
  ConferenceKeys,
  ConferenceRegistrationAddress,
  ConferenceRegistrationAddressKeys,
  ConferenceRegistrationAwardName,
  ConferenceRegistrationAwardNameKeys,
  ConferenceRegistrationEmail,
  ConferenceRegistrationEmailKeys,
  ConferenceRegistrationExcursion,
  ConferenceRegistrationExcursionsKeys,
  ConferenceRegistrationGuestsConfiguration,
  ConferenceRegistrationGuestsConfigurationKeys,
  ConferenceRegistrationPersonalInfo,
  ConferenceRegistrationPersonalInfoKeys,
  ConferenceRegistrationRSVP,
  ConferenceRegistrationRSVPKeys,
  ConferenceRegistrationStepName,
  conferenceRegistrationStepsMap,
  ConferenceStepsConfigurationKeys,
  FlightInfoKeys,
  GuestData,
  GuestEvents,
  GuestPlaceholderIds,
  HotelReservationKeys,
  InviteeStatus,
  Registrant,
  RegistrantData,
  RegistrantGuestFlightConfigurationSteps,
  RegistrantKeys,
  RegistrantModelKeys,
  RegistrantWizardStateKeys,
  RequestOutcomeKeys,
  TravelMode,
  WizardStepState,
} from '@ag-common-lib/public-api.functions';
import { startWith } from 'rxjs';
import { RegistrantChairmanExtensionKeys } from '@ag-common-lib/lib/models/registration/registrant-chairman-extension';
import { ChairmanExtensionConfigurationKeys } from '@ag-common-lib/lib/models/registration/conference-chairman-extension';

export function pickRSVPData(data: RegistrantData): ConferenceRegistrationRSVP {
  return pick(data, ConferenceRegistrationRSVPKeys);
}

export function pickGuestsConfigurationData(data: RegistrantData): ConferenceRegistrationGuestsConfiguration {
  return pick(data, ConferenceRegistrationGuestsConfigurationKeys);
}

export function pickPersonalInfoData(data: RegistrantData): ConferenceRegistrationPersonalInfo {
  return pick(data, ConferenceRegistrationPersonalInfoKeys);
}

export function pickEmailData(data: RegistrantData): ConferenceRegistrationEmail {
  return pick(data, ConferenceRegistrationEmailKeys);
}

export function pickAddressesData(data: RegistrantData): ConferenceRegistrationAddress {
  return pick(data, ConferenceRegistrationAddressKeys);
}

export function pickExcursionData(data: RegistrantData): ConferenceRegistrationExcursion {
  return pick(data, ConferenceRegistrationExcursionsKeys);
}

export function pickAddressData(address: Address) {
  return pick(address, [
    AddressModelKeys.address1,
    AddressModelKeys.address2,
    AddressModelKeys.city,
    AddressModelKeys.state,
    AddressModelKeys.zip,
    AddressModelKeys.country,
    AddressModelKeys.county,
    AddressModelKeys.isPrimaryBilling,
    AddressModelKeys.isPrimaryShipping,
  ]);
}

export function pickAwardNameData(data: RegistrantData): ConferenceRegistrationAwardName {
  return pick(data, ConferenceRegistrationAwardNameKeys);
}

export const isRequestedDifferentHotelCheckInCheckOutDates = (
  arrivalDate: string,
  departureDate: string,
  requestedArrivalDate: string,
  requestedDepartureDate: string,
): boolean => {
  const arrivalDateFsn = toDate(arrivalDate);
  const departureDateFsn = toDate(departureDate);
  const requestedArrivalDateFsn = toDate(requestedArrivalDate);
  const requestedDepartureDateFsn = toDate(requestedDepartureDate);
  if (![arrivalDateFsn, departureDateFsn, requestedArrivalDateFsn, requestedDepartureDateFsn].every(isValid)) {
    return false;
  }

  return !isSameDay(arrivalDateFsn, requestedArrivalDateFsn) || !isSameDay(departureDateFsn, requestedDepartureDateFsn);
};

export const calculateWizardState = (
  registrant: Registrant,
  conference: Conference = null,
  // Callers that already computed the current step (see the Attendee constructor) pass it in.
  // getCurrentStepInProgress is not cheap and this used to run it a second time for every row.
  precomputedStepInProgress?: ReturnType<typeof getCurrentStepInProgress>,
) => {
  const wizardState = registrant?.[RegistrantModelKeys.wizardState];
  const isSubmitted = !!wizardState?.[RegistrantWizardStateKeys.isSubmitted];

  const stepInProgress =
    precomputedStepInProgress ??
    getCurrentStepInProgress({
      conference,
      conferenceRegistration: registrant,
    });

  const registrantData = registrant?.[RegistrantModelKeys.data];
  const guestsIdsHasDifferTravelDatesRequest = registrant?.[RegistrantModelKeys.guestsIdsHasDifferTravelDatesRequest];
  const guestsEnabled = conference?.[ConferenceKeys.guestsEnabled];
  const guestsStepIsDone =
    wizardState?.[ConferenceRegistrationStepName.registrantGuestConfigurationStep] === WizardStepState.done;
  const flightsEnabled = conference?.[ConferenceKeys.flightsEnabled];
  const flightsStepIsDone =
    wizardState?.[ConferenceRegistrationStepName.registrantFlightInformationStep] === WizardStepState.done;
  const hotelEnabled = conference?.[ConferenceKeys.hotelEnabled];
  const hotelReservationStepIsDone =
    wizardState?.[ConferenceRegistrationStepName.registrantHotelReservationStep] === WizardStepState.done;

  // Only surface "Support Requested" after the registrant has submitted the registration.
  // During the wizard, these flags can be set/changed as the agent configures guests/events.
  if (isSubmitted) {
    const supportItemsNotArchived = registrant?.[RegistrantModelKeys.statistic]?.supportItemsNotArchived;
    if (!!supportItemsNotArchived) {
      return AttendeeWizardState.supportRequested;
    }

    if (guestsEnabled && guestsStepIsDone && checkAdditionalGuestRequestOutcomeState(registrantData)) {
      return AttendeeWizardState.supportRequested;
    }

    if (flightsEnabled && flightsStepIsDone && checkNotResolvedFlightTravelDatesDiffer(registrantData)) {
      return AttendeeWizardState.supportRequested;
    }

    // Guest travel-date support requests are tracked by id; if any exist post-submit, treat as support requested.
    if (guestsIdsHasDifferTravelDatesRequest?.length) {
      return AttendeeWizardState.supportRequested;
    }

    if (hotelEnabled && hotelReservationStepIsDone && checkHotelReservationRequestOutcomeState(registrantData)) {
      return AttendeeWizardState.supportRequested;
    }
  }

  if (stepInProgress?.name === ConferenceRegistrationStepName.registrationSummaryStep) {
    return AttendeeWizardState.completedAndSubmitted;
  }

  if (!wizardState) {
    return AttendeeWizardState.initial;
  }

  if (isSubmitted) {
    return AttendeeWizardState.initialRegistrationSubmitted;
  }

  return AttendeeWizardState.inProgress;
};

export const checkAdditionalGuestRequestOutcomeState = (registrantData: RegistrantData): boolean => {
  // Request for additional Guests
  return (
    registrantData?.[RegistrantKeys.additionalGuestRequested] &&
    !registrantData?.[RegistrantKeys.additionalGuestRequestOutcome]?.[RequestOutcomeKeys.state]
  );
};

export const checkNotResolvedFlightTravelDatesDiffer = (registrantData: RegistrantData): boolean => {
  // Request for different Travel Dates
  const flightInformation = registrantData?.[RegistrantKeys.flightInformation];
  return (
    flightInformation?.[FlightInfoKeys.travelDatesDiffer] &&
    !flightInformation?.[FlightInfoKeys.travelDatesDifferRequestOutcome]?.[RequestOutcomeKeys.state]
  );
};

export const checkHotelReservationRequestOutcomeState = (registrantData: RegistrantData): boolean => {
  const hotelReservation = registrantData?.[RegistrantKeys.hotelReservation];
  // Request for different Additional Rooms
  const hasNotResolvedDifferentAdditionalRooms =
    hotelReservation?.[HotelReservationKeys.additionalRoomRequested] &&
    !hotelReservation?.[HotelReservationKeys.additionalRoomRequestOutcome]?.[RequestOutcomeKeys.state];
  // Request for different Hotel Booking Dates
  const hasDifferentTravelDates = isRequestedDifferentHotelCheckInCheckOutDates(
    hotelReservation?.[HotelReservationKeys.checkInDate],
    hotelReservation?.[HotelReservationKeys.checkOutDate],
    hotelReservation?.[HotelReservationKeys.requestedCheckInDate],
    hotelReservation?.[HotelReservationKeys.requestedCheckOutDate],
  );
  const hasNotResolvedDifferentHotelBookingDates =
    hasDifferentTravelDates &&
    !hotelReservation?.[HotelReservationKeys.requestDifferBookingDatesOutcome]?.[RequestOutcomeKeys.state];
  return [hasNotResolvedDifferentAdditionalRooms, hasNotResolvedDifferentHotelBookingDates].some(Boolean);
};

export function calculateSummaryStepWizardState(
  registrantData: RegistrantData,
  conference: Conference,
): AttendeeWizardState {
  if (conference?.[ConferenceKeys.guestsEnabled] && checkAdditionalGuestRequestOutcomeState(registrantData)) {
    return AttendeeWizardState.supportRequested;
  }

  if (conference?.[ConferenceKeys.flightsEnabled] && checkNotResolvedFlightTravelDatesDiffer(registrantData)) {
    return AttendeeWizardState.supportRequested;
  }

  if (conference?.[ConferenceKeys.hotelEnabled] && checkHotelReservationRequestOutcomeState(registrantData)) {
    return AttendeeWizardState.supportRequested;
  }

  return AttendeeWizardState.completedAndSubmitted;
}

export function prepareStepsToComplete({
  conference,
  conferenceRegistration,
}: {
  conference: Conference;
  conferenceRegistration: Registrant;
}): any {
  const wizardState = conferenceRegistration?.[RegistrantModelKeys.wizardState] ?? {};
  const guestsIds = Object.keys(conferenceRegistration?.[RegistrantModelKeys.guestIdsMap] ?? {});
  const state = {};

  conferenceRegistrationStepsMap.forEach((step, stepName) => {
    switch (stepName) {
      case ConferenceRegistrationStepName.rsvpStep:
      case ConferenceRegistrationStepName.registrationConfirmationStep:
      case ConferenceRegistrationStepName.registrantPaymentStep:
        if (wizardState?.[stepName] !== WizardStepState.done) {
          Object.assign(state, {
            [stepName]: WizardStepState.skipped,
          });
        }

        return;

      case ConferenceRegistrationStepName.registrantPersonalInformationStep:
        step?.steps?.forEach(subStep => {
          const subStepName = subStep?.name;
          if (wizardState?.[subStepName] !== WizardStepState.done) {
            Object.assign(state, {
              [subStepName]: WizardStepState.skipped,
            });
          }
        });
        return;

      case ConferenceRegistrationStepName.registrantHotelReservationStep:
        if (conference?.[ConferenceKeys.hotelActive] && wizardState?.[stepName] !== WizardStepState.done) {
          Object.assign(state, {
            [stepName]: WizardStepState.skipped,
          });
        }
        return;

      case ConferenceRegistrationStepName.registrantFlightInformationStep:
        if (wizardState?.[ConferenceRegistrationStepName.registrantFlightYourSelectionStep] !== WizardStepState.done) {
          Object.assign(state, {
            [ConferenceRegistrationStepName.registrantFlightYourSelectionStep]: WizardStepState.skipped,
          });
        }

        guestsIds
          ?.map(guestId => ({
            data: {
              registrantGuestDbId: guestId,
              rootStepName: stepName,
            },
            name: `${stepName}_${guestId}`,
          }))
          ?.forEach(guestStep => {
            if (wizardState?.[guestStep.name] !== WizardStepState.done) {
              Object.assign(state, {
                [guestStep.name]: WizardStepState.skipped,
              });
            }
          });

        return;

      case ConferenceRegistrationStepName.registrantExcursionsStep:
        if (!conference?.[ConferenceKeys.excursionsActive]) {
          return;
        }
        if (wizardState?.[stepName] !== WizardStepState.done) {
          Object.assign(state, {
            [stepName]: WizardStepState.skipped,
          });
        }

        if (
          wizardState?.[ConferenceRegistrationStepName.registrantExcursionYourSelectionStep] !== WizardStepState.done
        ) {
          Object.assign(state, {
            [ConferenceRegistrationStepName.registrantExcursionYourSelectionStep]: WizardStepState.skipped,
          });
        }

        guestsIds
          ?.map(guestId => ({
            data: {
              registrantGuestDbId: guestId,
              rootStepName: stepName,
            },
            name: `${stepName}_${guestId}`,
          }))
          ?.forEach(guestStep => {
            if (wizardState?.[guestStep.name] !== WizardStepState.done) {
              Object.assign(state, {
                [guestStep.name]: WizardStepState.skipped,
              });
            }
          });

        return;

      case ConferenceRegistrationStepName.registrantGuestConfigurationStep:
        if (!conference?.[ConferenceKeys.guestsActive]) {
          return;
        }

        if (wizardState?.[stepName] !== WizardStepState.done) {
          Object.assign(state, {
            [stepName]: WizardStepState.skipped,
          });
        }

        guestsIds
          ?.map(guestId => ({
            data: {
              registrantGuestDbId: guestId,
              rootStepName: stepName,
            },
            name: `${stepName}_${guestId}`,
          }))
          ?.forEach(guestStep => {
            if (wizardState?.[guestStep.name] !== WizardStepState.done) {
              Object.assign(state, {
                [guestStep.name]: WizardStepState.skipped,
              });
            }
          });
        return;

      default:
        break;
    }
  });

  return state;
}

export function getCurrentStepInProgress({
  currentGuestIdsMap,
  conference,
  conferenceRegistration,
}: {
  currentGuestIdsMap?: Record<string, GuestEvents[]>;
  conference: Conference;
  conferenceRegistration: Registrant;
}): {
  name: any;
  data?: {
    rootStepName?: ConferenceRegistrationStepName;
    registrantGuestDbId?: string;
    guestTemplate?: Partial<GuestData>;
  };
  title?: string;
} {
  const wizardState = conferenceRegistration?.[RegistrantModelKeys.wizardState] ?? {};
  const registrantData = conferenceRegistration?.[RegistrantModelKeys.data];
  const isSubmitted = !!wizardState?.[RegistrantWizardStateKeys.isSubmitted];
  const chairmanExtension = registrantData?.[RegistrantKeys.chairmanExtension];
  const chairmanExtensionInviteeStatus = chairmanExtension?.[RegistrantChairmanExtensionKeys.inviteeStatus];
  const isChairmanAccepted = chairmanExtensionInviteeStatus === InviteeStatus.accepted;
  const numberOfGuests = registrantData?.[RegistrantKeys.numberOfGuests] ?? 0;
  const chairmanExtensionNumberOfGuests = isChairmanAccepted
    ? registrantData?.[RegistrantKeys.chairmanExtension]?.[RegistrantChairmanExtensionKeys.numberOfGuests] ?? 0
    : 0;
  const guestsIdsMap = currentGuestIdsMap ?? conferenceRegistration?.[RegistrantModelKeys.guestIdsMap] ?? {};
  const allGuestIds = Object.keys(guestsIdsMap);
  const mainEventGuestIds = [];
  const chairmanExtGuestIds = [];

  Object.entries(guestsIdsMap)?.forEach(([guestDbId, guestEvents]) => {
    const guestEventsSet = new Set(guestEvents);
    if (guestEventsSet.has(GuestEvents.main)) {
      mainEventGuestIds.push(guestDbId);
    }
    if (guestEventsSet.has(GuestEvents.chairmanExtension)) {
      chairmanExtGuestIds.push(guestDbId);
    }
  });

  const stepNameTitleMap = new Map<ConferenceRegistrationStepName, string>([
    [ConferenceRegistrationStepName.rsvpStep, 'Initial'],
    [ConferenceRegistrationStepName.registrantPersonalInformationStep, 'In Progress: Personal Information'],
    [ConferenceRegistrationStepName.registrantGuestConfigurationStep, 'In Progress: Guest(s) Information'],
    [ConferenceRegistrationStepName.registrantHotelReservationStep, 'In Progress: Hotel Reservation'],
    [ConferenceRegistrationStepName.registrantFlightInformationStep, 'In Progress: Flight Information'],
    [ConferenceRegistrationStepName.registrantExcursionsStep, 'In Progress: Excursions'],
    [ConferenceRegistrationStepName.registrationConfirmationStep, 'In Progress: Confirmation'],
    [ConferenceRegistrationStepName.registrantPaymentStep, 'In Progress: Payment'],
    [ConferenceRegistrationStepName.registrationSummaryStep, 'Done'],
  ]);

  const inviteeStatus = registrantData?.[RegistrantKeys.inviteeStatus];

  if (inviteeStatus === InviteeStatus.declined) {
    return { title: 'Done: Declined', name: ConferenceRegistrationStepName.registrationSummaryStep };
  }

  if (isSubmitted) {
    return { title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrationSummaryStep), name: ConferenceRegistrationStepName.registrationSummaryStep };
  }

  for (const [stepName, step] of Array.from(conferenceRegistrationStepsMap.entries())) {
    const stepState = wizardState?.[stepName];
    switch (stepName) {
      case ConferenceRegistrationStepName.rsvpStep:
        if (!stepState || stepState === WizardStepState.initial) {
          return Object.assign({ title: stepNameTitleMap.get(stepName) }, { name: stepName });
        }
        break;
      case ConferenceRegistrationStepName.registrationConfirmationStep:
      case ConferenceRegistrationStepName.registrationSummaryStep:
      case ConferenceRegistrationStepName.registrantPaymentStep:
        if (!stepState || stepState === WizardStepState.initial) {
          return Object.assign({ title: stepNameTitleMap.get(stepName) }, { name: stepName });
        }
        break;
      case ConferenceRegistrationStepName.registrantPersonalInformationStep:
        const personalInfoSubSteps = step?.steps.filter(subStep => {
          const excludedSections = getExcludedSections(conference, stepName) as ConferenceRegistrationStepName[];
          return !excludedSections.includes(subStep.name as ConferenceRegistrationStepName);
        });
        const personalInformationSubStep = personalInfoSubSteps.find(subStep => !wizardState?.[subStep?.name]);
        if (personalInformationSubStep) {
          return Object.assign({}, personalInformationSubStep, {
            title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrantPersonalInformationStep),
          });
        }
        break;
      case ConferenceRegistrationStepName.registrantHotelReservationStep:
        if (conference?.[ConferenceKeys.hotelActive] && (!stepState || stepState === WizardStepState.initial)) {
          return Object.assign({ title: stepNameTitleMap.get(stepName) }, { name: stepName });
        }
        break;
      case ConferenceRegistrationStepName.registrantFlightInformationStep:
        if (!conference?.[ConferenceKeys.flightsActive]) {
          break;
        }
        if (!wizardState?.[ConferenceRegistrationStepName.registrantFlightYourSelectionStep]) {
          return Object.assign(
            { title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrantFlightInformationStep) },
            { name: ConferenceRegistrationStepName.registrantFlightYourSelectionStep },
          );
        }

        if (wizardState?.[ConferenceRegistrationStepName.registrantFlightYourSelectionStep] === WizardStepState.done) {
          break;
        }
        const flightInformation =
          conferenceRegistration?.[RegistrantModelKeys.data]?.[RegistrantKeys.flightInformation];

        const equalTravelMode = flightInformation?.[FlightInfoKeys.equalTravelMode];
        const travelMode = flightInformation?.[FlightInfoKeys.travelMode];
        const isBookOwnFlights = flightInformation?.[FlightInfoKeys.isBookOwnFlights];

        if (equalTravelMode && (isBookOwnFlights || travelMode === TravelMode.driving)) {
          break;
        }

        const guestFlightStep = allGuestIds
          ?.map(guestId => ({ data: { registrantGuestDbId: guestId }, name: `${stepName}_${guestId}` }))
          ?.find(guestStep => {
            const stepState = wizardState?.[guestStep?.name];
            return !stepState || stepState === WizardStepState.initial;
          });

        if (guestFlightStep) {
          return Object.assign({
            title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrantFlightInformationStep),
            name: `${stepName}_${GuestPlaceholderIds.active}`,
          });
        }

        break;
      case ConferenceRegistrationStepName.registrantChairmanExtExcursionsStep:
        if (!conference?.[ConferenceKeys.excursionsActive]) {
          break;
        }
        const chairmanExtConfiguration = conference?.[ConferenceKeys.chairmanExtensionConfiguration];
        const chairmanExtExcursions = chairmanExtConfiguration?.[ChairmanExtensionConfigurationKeys.excursions];

        if (!chairmanExtExcursions?.length) {
          break;
        }

        if (chairmanExtensionInviteeStatus !== InviteeStatus.accepted) {
          break;
        }

        if (!stepState || stepState === WizardStepState.initial) {
          return {
            name: stepName,
            title: stepNameTitleMap.get(stepName),
          };
        }

        if (stepState === WizardStepState.done) {
          break;
        }

        const chairmanExtExcursionsYourSelectionWizardState =
          wizardState?.[ConferenceRegistrationStepName.registrantChairmanExtExcursionYourSelectionStep];
        if (
          !chairmanExtExcursionsYourSelectionWizardState ||
          chairmanExtExcursionsYourSelectionWizardState === WizardStepState.initial
        ) {
          return Object.assign(
            { title: stepNameTitleMap.get(stepName) },
            { name: ConferenceRegistrationStepName.registrantChairmanExtExcursionYourSelectionStep },
          );
        }

        const chairmanExtGuestExcursionStep = chairmanExtGuestIds
          ?.map(guestId => ({ data: { registrantGuestDbId: guestId }, name: `${stepName}_${guestId}` }))
          ?.find(guestStep => {
            const stepState = wizardState?.[guestStep?.name];
            return !stepState || stepState === WizardStepState.initial;
          });

        if (chairmanExtGuestExcursionStep) {
          return Object.assign({
            title: stepNameTitleMap.get(stepName),
            name: `${stepName}_${GuestPlaceholderIds.active}`,
          });
        }

        break;
      case ConferenceRegistrationStepName.registrantExcursionsStep:
        if (!conference?.[ConferenceKeys.excursionsActive]) {
          break;
        }

        if (!conference?.[ConferenceKeys.excursions]?.length) {
          break;
        }
        if (!stepState || stepState === WizardStepState.initial) {
          return {
            name: stepName,
            title: stepNameTitleMap.get(stepName),
          };
        }

        if (stepState === WizardStepState.done) {
          break;
        }

        const mainEventExcursionsYourSelectionWizardState =
          wizardState?.[ConferenceRegistrationStepName.registrantExcursionYourSelectionStep];
        if (
          !mainEventExcursionsYourSelectionWizardState ||
          mainEventExcursionsYourSelectionWizardState === WizardStepState.initial
        ) {
          return Object.assign(
            { title: stepNameTitleMap.get(stepName) },
            { name: ConferenceRegistrationStepName.registrantExcursionYourSelectionStep },
          );
        }

        const mainEventGuestExcursionStep = mainEventGuestIds
          ?.map(guestId => ({ data: { registrantGuestDbId: guestId }, name: `${stepName}_${guestId}` }))
          ?.find(guestStep => {
            const stepState = wizardState?.[guestStep?.name];
            return !stepState || stepState === WizardStepState.initial;
          });

        if (mainEventGuestExcursionStep) {
          return Object.assign({
            title: stepNameTitleMap.get(stepName),
            name: `${stepName}_${GuestPlaceholderIds.active}`,
          });
        }

        break;
      case ConferenceRegistrationStepName.registrantGuestConfigurationStep:
        if (!conference?.[ConferenceKeys.guestsActive]) {
          break;
        }

        if (!stepState || stepState === WizardStepState.initial) {
          return Object.assign({ title: stepNameTitleMap.get(stepName) }, { name: stepName });
        }

        if (stepState === WizardStepState.done) {
          break;
        }

        const totalDoneGuestDataSteps = mainEventGuestIds
          ?.map(guestId => `${stepName}_${guestId}`)
          ?.filter(key => {
            const keyStep = wizardState?.[key];
            return !!keyStep;
          })?.length;

        if (!numberOfGuests) {
          break;
        }

        if (totalDoneGuestDataSteps < numberOfGuests) {
          return Object.assign(
            { title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrantGuestConfigurationStep) },
            { name: `${stepName}_${GuestPlaceholderIds.active}`, data: { rootStepName: stepName } },
          );
        }
        break;
      case ConferenceRegistrationStepName.registrantChairmanExtensionGuestsStep:
        if (!conference?.[ConferenceKeys.guestsActive]) {
          break;
        }

        const totalDoneExtensionGuestDataSteps = chairmanExtGuestIds
          ?.map(guestId => `${stepName}_${guestId}`)
          ?.filter(key => wizardState?.[key])?.length;

        if (totalDoneExtensionGuestDataSteps < chairmanExtensionNumberOfGuests) {
          return Object.assign(
            { title: stepNameTitleMap.get(ConferenceRegistrationStepName.registrantChairmanExtensionGuestsStep) },
            { name: `${stepName}_${GuestPlaceholderIds.active}` },
          );
        }
        break;
    }
  }

  return null;
}

const getExcludedSections = (conference: Conference, stepName) =>
  conference?.[ConferenceKeys.stepsConfiguration]?.find(
    config => config[ConferenceStepsConfigurationKeys.stepName] === stepName,
  )?.[ConferenceStepsConfigurationKeys.excludedSections] ?? [];
